import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import Navbar from "../components/Navbar";

const STATUS_CFG = {
  pending: {
    label: "Pending",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    icon: "🕐",
  },
  confirmed: {
    label: "Confirmed",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.1)",
    icon: "✅",
  },
  preparing: {
    label: "Preparing",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.1)",
    icon: "👨‍🍳",
  },
  shipped: {
    label: "Shipped",
    color: "#f97316",
    bg: "rgba(249,115,22,0.1)",
    icon: "📦",
  },
  out_for_delivery: {
    label: "Out for Delivery",
    color: "#06b6d4",
    bg: "rgba(6,182,212,0.1)",
    icon: "🚴",
  },
  delivered: {
    label: "Delivered",
    color: "#16a34a",
    bg: "rgba(22,163,74,0.1)",
    icon: "🎉",
  },
  cancelled: {
    label: "Cancelled",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    icon: "✕",
  },
};

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confetti, setConfetti] = useState(true);

  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((res) => setOrder(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    const t = setTimeout(() => setConfetti(false), 3000);
    return () => clearTimeout(t);
  }, [orderId]);

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f8fafb",
          fontFamily: "'Roboto',sans-serif",
        }}
      >
        <Navbar />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "3px solid #e5e7eb",
              borderTopColor: "#2D8B4E",
              animation: "spin 0.7s linear infinite",
            }}
          />
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );

  const cfg = STATUS_CFG[order?.orderStatus] || STATUS_CFG.confirmed;
  const isPaid = order?.paymentStatus === "paid";
  const isRazorpay = order?.paymentMethod === "razorpay";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafb",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <Navbar />

      {/* confetti dots */}
      {confetti && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 99,
            overflow: "hidden",
          }}
        >
          {[...Array(22)].map((_, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: [
                  "#2D8B4E",
                  "#4CAF72",
                  "#fbbf24",
                  "#f97316",
                  "#06b6d4",
                  "#8b5cf6",
                ][i % 6],
                left: `${(i * 4.5) % 100}%`,
                animation: `confetti-fall ${1.5 + (i % 4) * 0.4}s ease-in forwards`,
                animationDelay: `${(i % 5) * 0.12}s`,
                top: "-10px",
              }}
            />
          ))}
        </div>
      )}

      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: "20px 20px 32px",
          boxSizing: "border-box",
        }}
      >
        {/* ── success hero ── */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: 28,
              boxShadow: "0 12px 40px rgba(45,139,78,0.35)",
              animation: "pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards",
            }}
          >
            🎉
          </div>
          <h1
            style={{
              fontSize: 21,
              fontWeight: 900,
              color: "#111827",
              margin: "0 0 8px",
              letterSpacing: "-0.4px",
            }}
          >
            Order Confirmed!
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#6b7280",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            {isRazorpay && isPaid
              ? "Payment received · Your order is being prepared right now 🍃"
              : "We've received your order and it's being prepared fresh for you 🍃"}
          </p>
        </div>

        {order && (
          <>
            {/* ── order summary card ── */}
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                border: "1px solid #f0f0f0",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                overflow: "hidden",
                marginBottom: 14,
              }}
            >
              {/* green top bar */}
              <div
                style={{
                  height: 4,
                  background: "linear-gradient(90deg,#2D8B4E,#4CAF72)",
                }}
              />

              {/* order meta */}
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: ".6px",
                        margin: "0 0 4px",
                      }}
                    >
                      Order ID
                    </p>
                    <p
                      style={{
                        fontSize: 16,
                        fontWeight: 900,
                        color: "#2D8B4E",
                        margin: 0,
                        letterSpacing: "-0.2px",
                      }}
                    >
                      #{order.orderId}
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        margin: "3px 0 0",
                      }}
                    >
                      {fmtDate(order.createdAt)}
                    </p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: ".6px",
                        margin: "0 0 4px",
                      }}
                    >
                      Status
                    </p>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        padding: "4px 12px",
                        borderRadius: 100,
                        background: cfg.bg,
                        color: cfg.color,
                        border: `1px solid ${cfg.color}30`,
                      }}
                    >
                      {cfg.icon} {cfg.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* bill row */}
              <div
                style={{
                  padding: "10px 16px",
                  borderBottom: "1px solid #f5f5f5",
                  display: "flex",
                  gap: 0,
                }}
              >
                {[
                  {
                    label: "Subtotal",
                    value: `₹${order.subtotal || order.total}`,
                  },
                  {
                    label: "Delivery",
                    value:
                      order.deliveryFee > 0 ? `₹${order.deliveryFee}` : "Free",
                  },
                  {
                    label: "Total Paid",
                    value: `₹${order.total}`,
                    highlight: true,
                  },
                ].map((r, i) => (
                  <div
                    key={r.label}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      borderRight: i < 2 ? "1px solid #f5f5f5" : "none",
                      padding: "0 8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 9,
                        color: "#9ca3af",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: ".5px",
                        margin: "0 0 2px",
                      }}
                    >
                      {r.label}
                    </p>
                    <p
                      style={{
                        fontSize: r.highlight ? 18 : 14,
                        fontWeight: 900,
                        color: r.highlight ? "#15803d" : "#374151",
                        margin: 0,
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {r.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* payment + delivery */}
              <div style={{ padding: "10px 16px", display: "flex", gap: 0 }}>
                <div
                  style={{
                    flex: 1,
                    borderRight: "1px solid #f5f5f5",
                    paddingRight: 16,
                  }}
                >
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: ".6px",
                      margin: "0 0 5px",
                    }}
                  >
                    Payment
                  </p>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span style={{ fontSize: 16 }}>
                      {isRazorpay ? "💳" : "💵"}
                    </span>
                    <div>
                      <p
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#111827",
                          margin: 0,
                        }}
                      >
                        {isRazorpay ? "Razorpay" : "Cash on Delivery"}
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          margin: 0,
                          fontWeight: 700,
                          color: isPaid ? "#16a34a" : "#d97706",
                        }}
                      >
                        {isPaid ? "✓ Paid" : "Pay on delivery"}
                      </p>
                    </div>
                  </div>
                </div>
                <div style={{ flex: 1, paddingLeft: 16 }}>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: ".6px",
                      margin: "0 0 5px",
                    }}
                  >
                    Delivery
                  </p>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span style={{ fontSize: 16 }}>🚴</span>
                    <div>
                      <p
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#111827",
                          margin: 0,
                        }}
                      >
                        ~2 Hours
                      </p>
                      <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>
                        {order.customerDetails?.address?.city || "Bengaluru"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* items strip */}
              {order.items?.length > 0 && (
                <div
                  style={{
                    padding: "8px 16px",
                    borderTop: "1px solid #f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: ".6px",
                      margin: 0,
                      flexShrink: 0,
                    }}
                  >
                    Items
                  </p>
                  <div style={{ display: "flex", gap: 6, flex: 1 }}>
                    {order.items.slice(0, 5).map((item, i) => (
                      <img
                        key={i}
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          objectFit: "cover",
                          border: "1.5px solid #f0f0f0",
                          flexShrink: 0,
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/32/e8f5e9/2D8B4E?text=🥤";
                        }}
                        title={item.name}
                      />
                    ))}
                    {order.items.length > 5 && (
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#6b7280",
                          flexShrink: 0,
                        }}
                      >
                        +{order.items.length - 5}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── what's next ── */}
            <div
              style={{
                background: "rgba(45,139,78,0.04)",
                borderRadius: 16,
                padding: "10px 14px",
                border: "1px solid rgba(45,139,78,0.12)",
                marginBottom: 12,
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#2D8B4E",
                  textTransform: "uppercase",
                  letterSpacing: ".6px",
                  margin: "0 0 6px",
                }}
              >
                What happens next
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  {
                    icon: "👨‍🍳",
                    text: "Our kitchen starts preparing your order fresh right now",
                  },
                  {
                    icon: "🚴",
                    text: "A delivery partner picks it up and heads your way",
                  },
                  {
                    icon: "🔔",
                    text: "You'll receive updates as your order progresses",
                  },
                ].map((s, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span style={{ fontSize: 14, flexShrink: 0 }}>
                      {s.icon}
                    </span>
                    <p style={{ fontSize: 12, color: "#374151", margin: 0 }}>
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── CTAs ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* primary — track order */}
              <button
                onClick={() => navigate("/orders")}
                style={{
                  width: "100%",
                  padding: "11px",
                  borderRadius: 14,
                  border: "none",
                  cursor: "pointer",
                  background: "linear-gradient(135deg,#15803d,#22c55e)",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 800,
                  boxShadow: "0 4px 18px rgba(21,128,61,0.3)",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 26px rgba(21,128,61,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow =
                    "0 4px 18px rgba(21,128,61,0.3)";
                }}
              >
                📦 Track Your Order
              </button>

              {/* secondary — order more */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                <button
                  onClick={() => navigate("/products")}
                  style={{
                    padding: "9px",
                    borderRadius: 10,
                    border: "1.5px solid rgba(45,139,78,0.25)",
                    background: "rgba(45,139,78,0.05)",
                    color: "#2D8B4E",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(45,139,78,0.12)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "rgba(45,139,78,0.05)")
                  }
                >
                  🥤 Order More
                </button>
                <button
                  onClick={() => navigate("/products?bestseller=true")}
                  style={{
                    padding: "9px",
                    borderRadius: 10,
                    border: "1.5px solid rgba(251,191,36,0.35)",
                    background: "rgba(251,191,36,0.05)",
                    color: "#d97706",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(251,191,36,0.12)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "rgba(251,191,36,0.05)")
                  }
                >
                  ⭐ Bestsellers
                </button>
              </div>

              {/* explore by section */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 6,
                }}
              >
                {[
                  { label: "Juices", emoji: "🥤", section: "juices" },
                  { label: "Salads", emoji: "🥗", section: "salads" },
                  { label: "Sandwiches", emoji: "🥪", section: "sandwiches" },
                  { label: "Smoothies", emoji: "🍓", section: "smoothies" },
                ].map((s) => (
                  <button
                    key={s.section}
                    onClick={() => navigate(`/products?section=${s.section}`)}
                    style={{
                      padding: "8px 4px",
                      borderRadius: 12,
                      border: "1px solid #f0f0f0",
                      background: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      transition: "all 0.2s",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 14px rgba(45,139,78,0.12)";
                      e.currentTarget.style.borderColor = "rgba(45,139,78,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "none";
                      e.currentTarget.style.boxShadow =
                        "0 1px 4px rgba(0,0,0,0.04)";
                      e.currentTarget.style.borderColor = "#f0f0f0";
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{s.emoji}</span>
                    <span
                      style={{ fontSize: 9, fontWeight: 700, color: "#374151" }}
                    >
                      {s.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin         { to { transform: rotate(360deg); } }
        @keyframes pop          { 0%{transform:scale(0);opacity:0} 100%{transform:scale(1);opacity:1} }
        @keyframes confetti-fall {
          0%   { transform: translateY(-10px) rotate(0deg);   opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
